/*
  Safir buy box extras. Plain deferred script, no libraries, loaded by
  sections/sg-buy.liquid. Everything here sits on top of the buy box that
  assets/safir-g.js runs; if this file fails, the buy box still works.

  - Flavor color: the buy box (and, on product pages, the whole page) takes
    the accent color of the flavor on screen.
  - Price in the add to cart button, kept in step with pack and delivery.
  - Free shipping meter under the button: what is already in the cart plus
    this pick, against the free shipping amount in theme settings.
  - Photos: arrows on each side and a swipe on a phone step through them;
    with a mouse, a click zooms in (never just hovering).
  - Sticky buy bar (product pages): once the add to cart button scrolls
    off the top, a bar with the photo, the pick and its price slides up.
    Its button presses the real one, so the 18+ check, cart drawer and the
    theme's own add animation all still run.
  - After an add: flavor colored drops burst out of the button.

  The buy box writes its choice into the form's hidden inputs (variant id
  and selling plan). Hidden inputs reflect .value into the attribute, so a
  MutationObserver on those two inputs sees every change, whoever made it.
  Nothing moves for people whose device asks for reduced motion.
*/
(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: no-preference)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 900px)');
  const FLAVORS = ['pina', 'strawberry', 'passion'];

  const flavorOf = (text) => {
    const t = String(text || '').toLowerCase();
    if (t.includes('colada') || t.includes('pina')) return 'pina';
    if (t.includes('strawberry')) return 'strawberry';
    if (t.includes('passion')) return 'passion';
    return '';
  };

  const colorOf = (flavor) =>
    getComputedStyle(document.documentElement).getPropertyValue(`--sr-${flavor}`).trim();

  const moneyFormatter = (format) => {
    const fmt = format || '${{amount}}';
    const delimit = (cents, decimals, thousands, decimal) => {
      const parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return (cents) => {
      const value = Math.round(Number(cents) || 0);
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        if (key === 'amount_no_decimals') return delimit(value, 0, ',', '.');
        if (key === 'amount_with_comma_separator') return delimit(value, 2, '.', ',');
        if (key === 'amount_no_decimals_with_comma_separator') return delimit(value, 0, '.', ',');
        if (key === 'amount_with_apostrophe_separator') return delimit(value, 2, "'", '.');
        return delimit(value, 2, ',', '.');
      });
    };
  };

  // Restart a one-shot animation class.
  const replay = (el, cls) => {
    if (!el || !motion.matches) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    setTimeout(() => el.classList.remove(cls), 900);
  };

  /* ---- Cart total, shared by every buy box ---------------------------- */

  const cart = { total: null, count: null, listeners: [] };

  const cartCount = () => {
    const el = document.querySelector('.cart-bubble__text-count');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  };

  const loadCart = () => {
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${base}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        cart.total = data.total_price;
        cart.listeners.forEach((fn) => fn());
      })
      .catch(() => {});
  };

  // The header bubble is redrawn by the theme on every cart change; when
  // its number moves, refresh the total and tell whoever is waiting for an add.
  let lastCount = cartCount();
  let lastAdd = null;
  let watching = false;
  const watchCart = () => {
    if (watching) return;
    watching = true;
    let queued = false;
    const check = () => {
      queued = false;
      const now = cartCount();
      if (now === lastCount) return;
      const grew = now > lastCount;
      lastCount = now;
      loadCart();
      if (grew && lastAdd && Date.now() - lastAdd.at < 8000) lastAdd.done();
      if (grew) lastAdd = null;
    };
    const bubble = document.querySelector('.cart-bubble__text-count');
    const scope = (bubble && bubble.closest('header, .shopify-section-group-header-group')) || document.body;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    }).observe(scope, { childList: true, subtree: true, characterData: true });
  };

  /* ---- One buy box ---------------------------------------------------- */

  function Box(root) {
    const dataEl = root.querySelector('script[data-sg-data]');
    if (!dataEl) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const byId = {};
    data.products.forEach((p) => {
      byId[String(p.id)] = p;
    });
    const money = moneyFormatter(data.moneyFormat);
    const form = root.querySelector('product-form-component');
    const idInput = root.querySelector('product-form-component input[name="id"]');
    const planInput = root.querySelector('[data-sg-plan-input]');
    const button = root.querySelector('product-form-component button[name="add"]');
    const meter = root.querySelector('[data-sr-ship]');
    const media = root.querySelector('.sg-buy__main');
    const isProduct = data.mode === 'product';
    if (!form || !idInput || !button) return;

    const pick = () => {
      const product = byId[String(form.dataset.productId)] || byId[String(data.start.productId)];
      if (!product) return null;
      const variant = product.variants.find((v) => String(v.id) === String(idInput.value));
      if (!variant) return null;
      const planId = planInput && !planInput.disabled ? planInput.value : '';
      const planned = planId && variant.plans && variant.plans[planId] != null;
      const plan = planned ? product.plans.find((p) => String(p.id) === String(planId)) : null;
      return {
        product,
        variant,
        price: planned ? variant.plans[planId] : variant.price,
        planLabel: planned ? data.text.subLabel || 'Subscribe & Save' : data.text.onceLabel || 'One-Time Purchase',
        planName: plan ? plan.label : '',
      };
    };

    /* Price inside the button */
    const priceEl = document.createElement('span');
    priceEl.className = 'sr-atc__price';
    priceEl.setAttribute('data-sr-atc-price', '');
    const textWrap = button.querySelector('.add-to-cart-text');
    if (textWrap) textWrap.append(priceEl);

    /* Sticky bar (product pages only) */
    let bar = null;
    if (isProduct) {
      bar = document.createElement('div');
      bar.className = 'sr-buybar';
      bar.setAttribute('aria-hidden', 'true');
      bar.inert = true;
      bar.innerHTML =
        '<span class="sr-buybar__media"><img alt="" width="96" height="96" decoding="async"></span>' +
        '<span class="sr-buybar__text"><strong class="sr-buybar__name"></strong><span class="sr-buybar__pick"></span></span>' +
        '<button type="button" class="sr-buybar__btn"><span class="sr-buybar__label"></span><span class="sr-buybar__price"></span></button>';
      document.body.append(bar);
      document.documentElement.classList.add('sr-has-buybar');
      const label = button.querySelector('.add-to-cart-text__content');
      bar.querySelector('.sr-buybar__label').textContent = (label && label.textContent.trim()) || 'Add to Cart';
      bar.querySelector('.sr-buybar__btn').addEventListener('click', () => {
        if (button.disabled) return;
        noteAdd(bar.querySelector('.sr-buybar__btn'));
        button.click();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          const e = entries[0];
          const show = !e.isIntersecting && e.boundingClientRect.top < 0;
          bar.classList.toggle('is-on', show);
          bar.inert = !show;
          if (show) bar.removeAttribute('aria-hidden');
          else bar.setAttribute('aria-hidden', 'true');
        }).observe(button);
      }
    }

    /* Burst after a successful add */
    const burst = (from) => {
      if (!motion.matches || !from || !from.isConnected) return;
      const r = from.getBoundingClientRect();
      if (!r.width) return;
      const flavor = root.dataset.srFlavor;
      const node = document.createElement('span');
      node.className = 'sr-burst';
      node.setAttribute('aria-hidden', 'true');
      node.style.left = `${r.left + r.width / 2}px`;
      node.style.top = `${r.top + r.height / 2}px`;
      node.style.setProperty('--sr-burst-w', `${Math.min(r.width, 420)}px`);
      for (let i = 0; i < 14; i += 1) {
        const drop = document.createElement('i');
        drop.style.setProperty('--a', `${i * (360 / 14) + (i % 2 ? 8 : -8)}deg`);
        drop.style.setProperty('--d', `${0.75 + (i % 3) * 0.18}`);
        drop.style.background = colorOf(flavor || FLAVORS[i % 3]);
        node.append(drop);
      }
      document.body.append(node);
      setTimeout(() => node.remove(), 1000);
      replay(from, 'sr-added');
    };

    const noteAdd = (from) => {
      watchCart();
      lastAdd = { at: Date.now(), done: () => burst(from.closest('.sr-buybar.is-on') ? from : button) };
    };
    button.addEventListener('click', () => noteAdd(button));

    /* Free shipping meter */
    const renderMeter = (p) => {
      if (!meter || !p) return;
      const threshold = Number(meter.dataset.threshold) || 0;
      if (!threshold) return;
      const base = cart.total == null ? Number(meter.dataset.cart) || 0 : cart.total;
      const total = base + p.price;
      const left = Math.max(threshold - total, 0);
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      meter.classList.toggle('is-free', left === 0);
      meter.style.setProperty('--sr-ship', `${pct}%`);
      const goal = meter.querySelector('[data-sr-ship-goal]');
      const status = meter.querySelector('[data-sr-ship-left]');
      if (status) {
        status.textContent = left === 0 ? meter.dataset.freeText : (meter.dataset.leftText || '[amount] to go').replace('[amount]', money(left));
      }
      if (goal && left === 0 && meter.dataset.freeGoal) goal.textContent = meter.dataset.freeGoal;
      else if (goal && meter.dataset.goal) goal.textContent = meter.dataset.goal;
    };
    cart.listeners.push(() => renderMeter(pick()));

    /* Everything that follows the pick */
    let lastPrice = null;
    let lastProduct = null;
    const sync = () => {
      const p = pick();
      if (!p) return;
      const priceText = money(p.price);
      if (priceEl.textContent !== priceText) {
        priceEl.textContent = priceText;
        if (lastPrice !== null) replay(priceEl, 'sr-tick');
        lastPrice = priceText;
      }

      if (lastProduct !== p.product.id) {
        lastProduct = p.product.id;
        const flavor = flavorOf(`${p.product.url} ${p.product.title}`);
        if (flavor) root.dataset.srFlavor = flavor;
        else delete root.dataset.srFlavor;
        if (isProduct && flavor) document.documentElement.dataset.srFlavor = flavor;
      }

      renderMeter(p);

      if (bar) {
        const img = bar.querySelector('img');
        const thumb = p.product.images[0] && p.product.images[0].thumb;
        if (thumb && img.getAttribute('src') !== thumb) img.src = thumb;
        bar.querySelector('.sr-buybar__name').textContent = p.product.heading || p.product.name || p.product.title;
        bar.querySelector('.sr-buybar__pick').textContent = [p.variant.label, p.planName || p.planLabel]
          .filter(Boolean)
          .join(' · ');
        const barPrice = bar.querySelector('.sr-buybar__price');
        if (barPrice.textContent !== priceText) {
          barPrice.textContent = priceText;
          replay(barPrice, 'sr-tick');
        }
        bar.querySelector('.sr-buybar__btn').disabled = button.disabled;
      }
    };

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        sync();
      }, 0);
    };
    const watch = new MutationObserver(schedule);
    watch.observe(idInput, { attributes: true, attributeFilter: ['value'] });
    if (planInput) watch.observe(planInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
    watch.observe(form, { attributes: true, attributeFilter: ['data-product-id'] });
    watch.observe(button, { attributes: true, attributeFilter: ['disabled'] });
    root.addEventListener('change', schedule);
    sync();

    /* Photos: arrows and swipe step through them; a mouse click zooms in */
    if (media) {
      const thumbsOf = () => [...root.querySelectorAll('[data-sg-thumb]')];
      // The thumbnails are the gallery's own controls (safir-g.js redraws
      // them per flavor), so stepping just presses the next one.
      const go = (step) => {
        const thumbs = thumbsOf();
        if (thumbs.length < 2) return;
        const at = Math.max(0, thumbs.findIndex((b) => b.getAttribute('aria-current') === 'true'));
        thumbs[(at + step + thumbs.length) % thumbs.length].click();
        replay(media, step > 0 ? 'sr-slide-next' : 'sr-slide-prev');
      };

      const unzoom = () => media.classList.remove('is-zoom');
      const aim = (event) => {
        const img = media.querySelector('img');
        if (!img) return;
        const r = media.getBoundingClientRect();
        img.style.transformOrigin = `${((event.clientX - r.left) / r.width) * 100}% ${((event.clientY - r.top) / r.height) * 100}%`;
      };

      const arrows = [-1, 1].map((step) => {
        const arrow = document.createElement('button');
        arrow.type = 'button';
        arrow.className = `sr-arrow sr-arrow--${step > 0 ? 'next' : 'prev'}`;
        arrow.setAttribute('aria-label', step > 0 ? 'Next photo' : 'Previous photo');
        arrow.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${step > 0 ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'}"/></svg>`;
        arrow.addEventListener('click', () => {
          unzoom();
          go(step);
        });
        media.append(arrow);
        return arrow;
      });
      const syncArrows = () => {
        const one = thumbsOf().length < 2;
        arrows.forEach((arrow) => {
          arrow.hidden = one;
        });
      };
      syncArrows();
      const thumbsEl = root.querySelector('[data-sg-thumbs]');
      if (thumbsEl) new MutationObserver(syncArrows).observe(thumbsEl, { childList: true });

      // Zoom only on click: click to zoom in where the pointer is, move to
      // look around, click again (or leave the photo, or press Esc) to zoom out.
      media.addEventListener('click', (event) => {
        if (!finePointer.matches || event.target.closest('.sr-arrow')) return;
        if (media.classList.contains('is-zoom')) {
          unzoom();
          return;
        }
        aim(event);
        media.classList.add('is-zoom');
      });
      media.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse' && media.classList.contains('is-zoom')) aim(event);
      });
      media.addEventListener('pointerleave', unzoom);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') unzoom();
      });

      let start = null;
      media.addEventListener(
        'touchstart',
        (event) => {
          const t = event.touches[0];
          start = event.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null;
        },
        { passive: true }
      );
      media.addEventListener(
        'touchend',
        (event) => {
          if (!start) return;
          const t = event.changedTouches[0];
          const dx = t.clientX - start.x;
          const dy = t.clientY - start.y;
          start = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          go(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-sg-buy]').forEach((root) => {
      if (root.dataset.srPdp) return;
      root.dataset.srPdp = '1';
      Box(root);
    });
    watchCart();
    loadCart();
  };

  // safir-g.js (loaded just before this file) renders the first state;
  // start on the next task so this reads the inputs after it. Not a frame
  // callback: those never run in a tab that opens in the background.
  const start = () => setTimeout(boot, 0);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
